import { useFormatter, useTranslations } from 'next-intl';
import React, { useEffect, useRef, useState } from 'react';
import {
  useLazySendAdAuctionBidQuery,
  useGetUserQuery,
  useLazySendAdAuctionAutoBidQuery,
  useLazyGetItemCostsQuery,
} from '@/src/api/api';
import { useSelector } from 'react-redux';
import { TfiReload } from 'react-icons/tfi';
import { RootState } from '@/src/redux/store';
import { userState } from '@/src/redux/userSlice/userSlice';
import QueryLink from '@components/QueryLink';
import { MdOutlineCheckCircle, MdPerson } from 'react-icons/md';
import {
  customerState,
  customerTypes,
} from '@/src/redux/customerSlice/customerSlice';
import useCountdown from '@/src/utils/useCountdown';
import Modal from '@components/Modal';
import { AuthenticationContent } from '@components/AuthenticationModal';
import { BiSolidFactory } from 'react-icons/bi';
import { useCustomerSetter } from '@/src/redux/customerSlice/customerHandlers';
import getDefaultCurrency from '@/src/utils/getDefaultCurrency';
import useFormatterPrice from '@/src/utils/formatter-price';
import useCurrencyConversion from '@/src/utils/useCurrencyConversion';
import { BsExclamationOctagonFill } from 'react-icons/bs';
import { TbMessageCircleQuestion } from 'react-icons/tb';
import { FaWindowClose } from 'react-icons/fa';
import useLocaleSuffix from '@/src/utils/useLocaleSuffix';
import getOption from '@/src/utils/getOption';
import VatInfoPopup from './VatInfoPopup';
import useIfDefaultCurrency from '@/src/utils/useIfDefaultCurrency';

export default function AuctionBidControllerTemplate({
  useBidQuery,
  minimalBid,
  increment,
  customerAutoBid = 0,
  auctionId,
  auctionEnd,
  inputName,
  label,
  sublabel,
  confirmLabel,
  ad,
  autofill = true,
  onSuccess,
}: {
  useBidQuery:
    | typeof useLazySendAdAuctionBidQuery
    | typeof useLazySendAdAuctionAutoBidQuery;
  minimalBid: number;
  increment: number;
  customerAutoBid?: number;
  auctionId: number;
  auctionEnd: number;
  inputName: string;
  label: string;
  sublabel?: string;
  confirmLabel: string;
  ad: any;
  autofill?: boolean;
  onSuccess?: (a?: any) => void;
}) {
  const t = useTranslations('AuctionBidController');
  const a = useTranslations('AuctionCurrentBid');
  const c = useTranslations('customer');
  const g = useTranslations('general');
  const getLocaleSuffix = useLocaleSuffix();
  const isDefaultCurrency = useIfDefaultCurrency();
  const formatter = useFormatter();
  const formatterPrice = useFormatterPrice();
  const convert = useCurrencyConversion();
  const [sendBid, { isFetching }] = useBidQuery();
  const [getItemCosts, { data: itemCosts, isFetching: isFetchingCosts }] =
    useLazyGetItemCostsQuery();
  const user: userState = useSelector((state: RootState) => {
    return state.user;
  });
  const customer: customerState = useSelector((state: RootState) => {
    return state.customer;
  });
  const { data: fetchedUser } = useGetUserQuery(user.userId, {
    skip: !user.loggedIn,
  });
  const inputRef = useRef(null as null | HTMLInputElement);
  const userData = fetchedUser?.data;
  const [userBid, setUserBid] = useState(autofill ? minimalBid : NaN);
  const [userPlacedBid, setUserPlacedBid] = useState(
    autofill ? minimalBid : NaN
  );
  const [cursorPosition, setCursorPosition] = useState(0);
  const [authModalOpen, setOpenAuthModal] = useState(false);
  const [isBeingEdited, setIsBeingEdited] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(!autofill);
  const [userBidString, setUserBidString] = useState(
    autofill ? formatter.number(minimalBid) : ''
  );
  const [userBidError, setUserBidError] = useState('');
  const [userBidWarning, setUserBidWarning] = useState('');
  const [bidTooLow, setBidTooLow] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [changeCustomer, setChangeCustomer] = useState(false);

  const countDown = useCountdown(auctionEnd);

  const companiesCount = userData?.company_customers?.length ?? 0;
  const privateCount = userData?.private_customer ? 1 : 0;
  const total = companiesCount + privateCount;
  const isPrivate = !!ad.customer?.is_private;

  const setCustomer = useCustomerSetter();
  const setCompany = (company: any) => {
    setCustomer({
      id: company.id,
      type: customerTypes.company,
      name: company.name,
    });
    setChangeCustomer(false);
  };
  const setPrivateCustomer = (customerId: number) => {
    setCustomer({
      id: customerId,
      type: customerTypes.private,
      name: `${user.firstName} ${user.lastName}`,
    });
    setChangeCustomer(false);
  };

  const bid = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!confirm) {
      setUserPlacedBid(userBid);
      setConfirm(true);
      return;
    }
    if (userBidError) {
      return;
    }
    if (userPlacedBid < minimalBid || userBid < minimalBid) {
      setBidTooLow(true);
      return;
    }
    const formData = new FormData(e.currentTarget);
    const formValues = Object.fromEntries(formData);
    formValues[inputName] = String(userPlacedBid);
    sendBid(formValues)
      .unwrap()
      .then((res: any) => {
        setUserBidError('');
        setConfirm(false);
        if (!autofill) {
          setUserBid(NaN);
          setUserBidString('');
        }
        if (onSuccess) {
          onSuccess(!autofill ? userBid : null);
        }
      })
      .catch((res: any) => {
        let msg = t('unknownError');
        if (res?.data?.message) {
          const message = res?.data?.message;
          const message_no_dots = message.split('.')[0];
          const translation = t(message_no_dots);
          msg = translation;
          if (translation === `AuctionBidController.${message_no_dots}`) {
            msg = message;
          }
          if (message.split('.')[1]) {
            msg = msg + ' ' + message.split('.')[1];
          }
        }
        setUserBidError(msg);
      });
  };

  const [timeoutId, setTimeoutId] = useState(null as NodeJS.Timeout | null);

  const updateUserBid = (
    e: React.FocusEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>,
    event: string
  ) => {
    const newBid = parseInt(e.currentTarget.value.replace(/\D/g, ''));
    const initCursorPosition = inputRef?.current?.selectionStart || 0;
    const positionFromEnd = inputRef?.current?.value?.length
      ? inputRef.current.value.length - initCursorPosition
      : 0;
    const formatted = newBid ? formatter.number(newBid) : '';
    const roundedBid = newBid - (newBid % increment);
    const rounded = roundedBid !== newBid;
    const belowMinimal = newBid < minimalBid;
    const belowAutoBid = customerAutoBid ? newBid <= customerAutoBid : false;
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    if (event === 'focus') {
      setIsBeingEdited(true);
    } else if (event === 'blur') {
      setIsBeingEdited(false);
      if (!newBid) {
        if (autofill) {
          setUserBid(minimalBid);
          setUserBidString(formatter.number(minimalBid));
          setUserBidError('');
          setUserBidWarning('');
          setBidTooLow(false);
          setIsButtonDisabled(false);
        } else {
          setUserBid(NaN);
          setUserBidString('');
          setUserBidError('');
          setUserBidWarning('');
          setBidTooLow(false);
          setIsButtonDisabled(true);
        }
      } else if (belowAutoBid) {
        setUserBid(customerAutoBid + increment);
        setUserBidString(formatter.number(customerAutoBid + increment));
        setUserBidError('');
        setBidTooLow(false);
        setUserBidWarning(
          t('bidLowerThanCurrentAuto', {
            bid: formatter.number(customerAutoBid),
          })
        );
        setTimeout(() => {
          setIsButtonDisabled(false);
        }, 1600);
      } else if (belowMinimal) {
        setUserBid(minimalBid);
        setUserBidString(formatter.number(minimalBid));
        setUserBidError('');
        setUserBidWarning('');
        setBidTooLow(true);
        setTimeout(() => {
          setIsButtonDisabled(false);
        }, 1600);
      } else if (rounded) {
        setUserBid(roundedBid);
        setUserBidString(formatter.number(roundedBid));
        setUserBidError('');
        setUserBidWarning(t('onlyIncrements', { increment }));
        setBidTooLow(false);
        setTimeout(() => {
          setIsButtonDisabled(false);
        }, 1600);
      } else {
        setUserBid(newBid);
        setUserBidString(formatted);
        setIsButtonDisabled(false);
        setUserBidError('');
        setUserBidWarning('');
        setBidTooLow(false);
      }
    } else if (event === 'change') {
      setUserBid(newBid);
      setUserBidError('');
      setUserBidWarning('');
      setBidTooLow(false);
      if (inputRef) {
        inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
      }
      if (belowAutoBid) {
        setIsButtonDisabled(true);
        setTimeoutId(
          setTimeout(
            () =>
              setUserBidWarning(
                t('bidLowerThanCurrentAuto', {
                  bid: formatter.number(customerAutoBid),
                })
              ),
            2000
          )
        );
      } else if (belowMinimal) {
        setIsButtonDisabled(true);
        setTimeoutId(setTimeout(() => setBidTooLow(true), 2000));
      } else if (rounded) {
        setIsButtonDisabled(true);
        setTimeoutId(
          setTimeout(
            () => setUserBidError(t('onlyIncrements', { increment })),
            2000
          )
        );
      } else {
        setIsButtonDisabled(false);
      }
      if (userBidString !== formatted) {
        setUserBidString(formatted);
        setCursorPosition(formatted.length - positionFromEnd);
      }
    }
  };

  useEffect(() => {
    if (isBeingEdited || !autofill) {
      return;
    }
    if (minimalBid > userBid) {
      setUserBid(minimalBid);
      setUserBidString(formatter.number(minimalBid));
    }
  }, [minimalBid, userBid, formatter, isBeingEdited, autofill]);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [userBidString, cursorPosition, inputRef]);

  if (!countDown) {
    return null;
  }

  const modal = confirm ? (
    <Modal theme='yellow' customClose={() => setConfirm(false)}>
      <div className='flex flex-col gap-2'>
        <p className='w-full text-center'>{c('biddingAs')}</p>
        <p className='flex flex-wrap items-center justify-center gap-2 text-xl font-semibold'>
          {customer.type === customerTypes.company ? (
            <BiSolidFactory />
          ) : (
            <MdPerson />
          )}
          {customer.name}
        </p>
        {total > 1 && (
          <button
            type='button'
            onClick={() => setChangeCustomer(!changeCustomer)}
            className='m-auto flex items-center justify-center gap-2 rounded bg-brand-dark p-1 px-4 text-sm hover:bg-positive hover:text-white'
          >
            <TfiReload />
            {c('changeCustomer')}
          </button>
        )}
        {changeCustomer && (
          <div className='-mt-3 flex flex-col rounded bg-brand-dark p-2 px-6 pb-0'>
            <p className='mb-2'>{c('otherCustomers')}:</p>
            {userData?.company_customers?.map((customerItem: any) => {
              if (
                customer.id === customerItem.id &&
                customer.type === customerTypes.company
              )
                return null;
              return (
                <button
                  type='button'
                  className='flex items-center gap-2 border-t border-dark py-2 hover:font-bold focus:font-bold'
                  key={customerItem.id}
                  onClick={() => {
                    setCompany(customerItem);
                  }}
                >
                  <BiSolidFactory />
                  {customerItem.name}
                </button>
              );
            })}
            {userData?.private_customer &&
              ((customer.id !== userData?.private_customer.id &&
                customer.type === customerTypes.private) ||
                customer.type !== customerTypes.private) && (
                <button
                  type='button'
                  className='flex items-center gap-2 border-t border-dark py-2 hover:font-bold focus:font-bold'
                  onClick={() => {
                    setPrivateCustomer(userData.private_customer.id);
                  }}
                >
                  <MdPerson />
                  {user.firstName} {user.lastName}
                </button>
              )}
          </div>
        )}
        <div className='my-2 border-b-2 border-t-2 border-dark px-2 py-3 text-center text-3xl font-bold'>
          {ad[getLocaleSuffix('title')]}
        </div>
        <span className='-mb-2 text-center font-bold'>
          {getDefaultCurrency()}
        </span>
        <span className='text-center text-5xl font-bold'>
          {formatter.number(userPlacedBid)}
        </span>
        {minimalBid > userPlacedBid && (
          <div className='mx-6 mb-4 flex items-center justify-center gap-2 rounded bg-brand-alt p-3 text-center text-2xl font-bold text-white'>
            <span className='shrink-0'>
              <FaWindowClose />
            </span>{' '}
            {t('minimalBidIsNow')}:{' '}
            {formatterPrice(minimalBid, process.env.NEXT_PUBLIC_DEFAULT_LOCALE)}
          </div>
        )}
        {!isDefaultCurrency && (
          <span className='-mt-1 text-center text-xl'>
            {a('approx', { price: formatterPrice(convert(userPlacedBid)) })}
          </span>
        )}
        {
          <div className='relative -mt-2 flex items-center justify-center gap-1 text-center uppercase'>
            {a('includingVat')}:{' '}
            {!isFetchingCosts &&
              itemCosts &&
              formatterPrice(
                itemCosts?.total?.price_incl_vat,
                String(getOption('DEFAULT_LOCALE'))
              )}
            <VatInfoPopup itemizedCosts={itemCosts} isPrivate={isPrivate} />
          </div>
        }
        <span className='mt-2 text-center text-xl font-bold'>
          {t('bidsAreBinding')}!
        </span>
        <button
          type='submit'
          disabled={!confirm || !!userBidError || minimalBid > userPlacedBid}
          className={`${!confirm || !!userBidError || minimalBid > userPlacedBid ? 'opacity-70' : 'hover:bg-positive'} mb-2 flex w-full items-center justify-center gap-2 rounded-r-sm bg-dark px-4 py-2 text-lg font-semibold uppercase text-white transition-all focus:outline-none`}
        >
          <MdOutlineCheckCircle />
          {confirmLabel}
        </button>
        {isFetching && <p className='mb-2 mt-1'>{t('placingBid')}</p>}
        {userBidError && <p className='mb-2 mt-1'>{userBidError}</p>}
        {bidTooLow && (
          <p className='mt-1 flex items-center gap-1 text-sm'>
            <span className='text-brand-alt'>
              <BsExclamationOctagonFill />
            </span>
            {t('bidTooLow', {
              bid: formatterPrice(
                minimalBid,
                process.env.NEXT_PUBLIC_DEFAULT_LOCALE
              ),
            })}
          </p>
        )}
        <button
          type='button'
          onClick={() => setConfirm(false)}
          className='border-2 border-black px-4 py-2 text-lg font-bold uppercase hover:border-brand-alt hover:text-brand-alt'
        >
          {g('cancel')}
        </button>
      </div>
    </Modal>
  ) : (
    ''
  );

  return (
    <div className='mb-2'>
      <label className='mb-1 text-sm' htmlFor={inputName}>
        {sublabel}
      </label>
      <form method='post' onSubmit={bid} className='flex flex-wrap text-dark'>
        <input type='hidden' name='auction_id' value={auctionId}></input>
        {customer.id && customer.type && (
          <>
            <input type='hidden' name='customer_id' value={customer.id}></input>
            <input type='hidden' name='customer_type' value={customer.type} />
          </>
        )}
        <input
          className='min-w-[8em] grow basis-1/2 rounded-l-sm border-b border-black px-4 py-2 text-right font-roboto-condensed text-xl focus:outline-none'
          onFocus={(e) => updateUserBid(e, 'focus')}
          onChange={(e) => updateUserBid(e, 'change')}
          onBlur={(e) => updateUserBid(e, 'blur')}
          value={userBidString}
          name={inputName}
          id={inputName}
          ref={inputRef}
        />
        {user.loggedIn && !!userData?.identity_verified_at && !!total && (
          <button
            type='button'
            onClick={() => {
              setConfirm(!confirm);
              setUserPlacedBid(userBid);
              getItemCosts({ id: ad.id, amount: userBid });
            }}
            disabled={isButtonDisabled}
            className={`grow basis-1/2 rounded-r-sm bg-dark px-4 py-2 text-center text-lg font-semibold uppercase text-white transition-all ${!isButtonDisabled ? 'hover:bg-positive focus:bg-positive focus:outline-none' : 'opacity-75'}`}
          >
            {label}
          </button>
        )}
        {user.loggedIn && !!userData?.identity_verified_at && !total && (
          <QueryLink
            query={{ modal: 'register-company' }}
            className='grow basis-1/2 rounded-r-sm bg-dark px-4 py-2 text-center text-lg font-semibold uppercase text-white transition-all hover:bg-brand-alt focus:outline-none'
          >
            {label}
          </QueryLink>
        )}
        {user.loggedIn && !!userData && !userData.identity_verified_at && (
          <>
            <button
              type='button'
              onClick={() => setOpenAuthModal(true)}
              className='grow basis-1/2 rounded-r-sm bg-dark px-4 py-2 text-center text-lg font-semibold uppercase text-white transition-all hover:bg-brand-alt focus:outline-none'
            >
              {label}
            </button>
            {authModalOpen && (
              <Modal customClose={() => setOpenAuthModal(false)}>
                <AuthenticationContent />
              </Modal>
            )}
          </>
        )}
        {!user.loggedIn && (
          <QueryLink
            query={{ modal: 'login' }}
            className='grow basis-1/2 rounded-r-sm bg-dark px-4 py-2 text-center text-lg font-semibold uppercase text-white transition-all hover:bg-brand-alt focus:outline-none'
          >
            {label}
          </QueryLink>
        )}
        {modal}
      </form>
      {isFetching && <p className='mb-2 mt-1'>{t('placingBid')}</p>}
      {userBidError && (
        <p className='mt-1 flex items-center gap-1 text-sm'>
          <span className='text-brand-alt'>
            <BsExclamationOctagonFill />
          </span>
          {userBidError}
        </p>
      )}
      {userBidWarning && (
        <p className='mt-1 flex items-center gap-1 text-sm'>
          <span className='text-dark'>
            <TbMessageCircleQuestion />
          </span>
          {userBidWarning}
        </p>
      )}
      {bidTooLow && (
        <p className='mt-1 flex items-center gap-1 text-sm'>
          <span className='text-brand-alt'>
            <BsExclamationOctagonFill />
          </span>
          {t('bidTooLow', {
            bid: formatterPrice(
              minimalBid,
              process.env.NEXT_PUBLIC_DEFAULT_LOCALE
            ),
          })}
        </p>
      )}
    </div>
  );
}
